import api from '@/lib/api';
import { mediaUrlForPlayback } from '@/lib/mediaUrls';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000').replace(/\/+$/, '');

function parseMediaAssetId(videoUrl) {
  const s = String(videoUrl || '').trim();
  const match = s.match(/\/api\/media\/([^/?#]+)/);
  return match ? match[1] : null;
}

async function blobToDataUrl(blob) {
  if (!blob || blob.size <= 0) return null;
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(typeof reader.result === 'string' ? reader.result : null);
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(blob);
  });
}

async function fetchFrameByAssetId(assetId, timeSec) {
  const at = Math.max(0, Number(timeSec) || 0);
  try {
    let token = '';
    try {
      token = localStorage.getItem('storycraft_token') || '';
    } catch {
      token = '';
    }
    const qs = new URLSearchParams({ at: String(at) });
    if (token) qs.set('access_token', token);
    const resp = await fetch(
      `${BACKEND_URL}/api/media/${encodeURIComponent(assetId)}/frame?${qs.toString()}`,
      {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      },
    );
    if (!resp.ok) return null;
    return blobToDataUrl(await resp.blob());
  } catch {
    return null;
  }
}

async function fetchFrameViaExtractApi(videoUrl, timeSec) {
  const at = Math.max(0, Number(timeSec) || 0);
  const candidates = [videoUrl, mediaUrlForPlayback(videoUrl)].filter(Boolean);
  for (const url of candidates) {
    try {
      const { data } = await api.post(
        '/media/extract-frame',
        { url, at },
        { responseType: 'blob' },
      );
      const dataUrl = await blobToDataUrl(data);
      if (dataUrl) return dataUrl;
    } catch {
      /* try next candidate */
    }
  }
  return null;
}

async function fetchServerVideoFrameDataUrl(videoUrl, timeSec) {
  const candidates = [videoUrl, mediaUrlForPlayback(videoUrl)].filter(Boolean);
  for (const candidate of candidates) {
    const assetId = parseMediaAssetId(candidate);
    if (assetId) {
      const fromAsset = await fetchFrameByAssetId(assetId, timeSec);
      if (fromAsset) return fromAsset;
    }
  }
  return fetchFrameViaExtractApi(videoUrl, timeSec);
}

function grabVideoFrameClientSide(videoUrl, timeSec) {
  const url = mediaUrlForPlayback(videoUrl);
  if (!url) return Promise.resolve(null);

  return new Promise((resolve) => {
    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.muted = true;
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.preload = 'auto';

    const finish = (dataUrl) => {
      try {
        video.pause();
        video.removeAttribute('src');
        video.load();
      } catch {
        /* ignore */
      }
      resolve(dataUrl);
    };

    const fail = () => finish(null);

    video.onerror = fail;

    const drawFrame = () => {
      try {
        const w = video.videoWidth;
        const h = video.videoHeight;
        if (!(w > 0 && h > 0)) {
          fail();
          return;
        }
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          fail();
          return;
        }
        ctx.drawImage(video, 0, 0, w, h);
        const jpeg = canvas.toDataURL('image/jpeg', 0.9);
        finish(jpeg);
      } catch {
        fail();
      }
    };

    video.onloadedmetadata = () => {
      const dur = Number.isFinite(video.duration) && video.duration > 0 ? video.duration : 0;
      const want = Number(timeSec) || 0;
      const t = dur > 0
        ? Math.max(0, Math.min(want, dur - 0.04))
        : Math.max(0, want);

      const onSeeked = () => {
        requestAnimationFrame(drawFrame);
      };
      video.addEventListener('seeked', onSeeked, { once: true });
      try {
        video.currentTime = t;
      } catch {
        fail();
      }
    };

    try {
      video.src = url;
    } catch {
      fail();
    }
  });
}

/**
 * Decode one frame from a remote video into a JPEG data URL (for UI previews).
 * Prefers same-origin server extraction (avoids S3 CORS).
 *
 * @param {string} videoUrl
 * @param {number} timeSec source timestamp in seconds
 * @returns {Promise<string | null>}
 */
export async function grabVideoFrameDataUrl(videoUrl, timeSec) {
  const serverFrame = await fetchServerVideoFrameDataUrl(videoUrl, timeSec);
  if (serverFrame) return serverFrame;
  return grabVideoFrameClientSide(videoUrl, timeSec);
}
